import classNames from "classnames";
import * as shadowMakers from "util/shadowMakers";
import { Props } from "./Shape";

export const theming = {
  default: {
    solid: {
      bg: "bg-slate-300 border-slate-300",
      bgActive: "bg-slate-500 border-slate-500",
      text: "text-neutral-900",
      textActive: "text-slate-400",
      shadowBg: "bg-slate-500",
      shadowBgActive: "bg-slate-700",
    },
    outline: {
      bg: "bg-transparent border-slate-300",
      bgActive: "bg-purple-500/25 border-purple-500",
      text: "text-slate-300",
      textActive: "text-purple-500",
      shadowBg: "bg-transparent",
      shadowBgActive: "bg-transparent",
    },
    shadowBorder: "border-slate-500",
    shadowBorderActive: "border-purple-700",
  },
  white: {
    solid: {
      bg: "bg-white border-white",
      bgActive: "bg-gray-500 border-gray-500",
      text: "text-neutral-900",
      textActive: "text-black",
      shadowBg: "bg-gray-500",
      shadowBgActive: "bg-gray-700",
    },
    outline: {
      bg: "bg-transparent border-white",
      bgActive: "bg-purple-500/25 border-purple-500",
      text: "text-white",
      textActive: "text-purple-500",
      shadowBg: "bg-transparent",
      shadowBgActive: "bg-transparent",
    },
    shadowBorder: "border-gray-500",
    shadowBorderActive: "border-purple-700",
  },
  yellow: {
    solid: {
      bg: "bg-yellow-400 border-yellow-400",
      bgActive: "bg-yellow-600 border-yellow-600",
      text: "text-neutral-900",
      textActive: "text-yellow-500",
      shadowBg: "bg-yellow-600",
      shadowBgActive: "bg-yellow-700",
    },
    outline: {
      bg: "bg-transparent border-yellow-400",
      bgActive: "bg-yellow-400/25 border-yellow-600",
      text: "text-yellow-400",
      textActive: "text-yellow-500",
      shadowBg: "bg-transparent",
      shadowBgActive: "bg-transparent",
    },
    shadowBorder: "border-yellow-600",
    shadowBorderActive: "border-yellow-800",
  },
  green: {
    solid: {
      bg: "bg-emerald-400 border-emerald-400",
      bgActive: "bg-emerald-600 border-emerald-600",
      text: "text-white",
      textActive: "text-emerald-500",
      shadowBg: "bg-emerald-600",
      shadowBgActive: "bg-emerald-700",
    },
    outline: {
      bg: "bg-transparent border-emerald-400",
      bgActive: "bg-emerald-400/25 border-emerald-600",
      text: "text-emerald-400",
      textActive: "text-emerald-500",
      shadowBg: "bg-transparent",
      shadowBgActive: "bg-transparent",
    },
    shadowBorder: "border-emerald-600",
    shadowBorderActive: "border-emerald-800",
  },
  blue: {
    solid: {
      bg: "bg-blue-400 border-blue-400",
      bgActive: "bg-blue-600 border-blue-600",
      text: "text-white",
      textActive: "text-blue-500",
      shadowBg: "bg-blue-600",
      shadowBgActive: "bg-blue-700",
    },
    outline: {
      bg: "bg-transparent border-blue-400",
      bgActive: "bg-blue-400/25 border-blue-600",
      text: "text-blue-400",
      textActive: "text-blue-500",
      shadowBg: "bg-transparent",
      shadowBgActive: "bg-transparent",
    },
    shadowBorder: "border-blue-600",
    shadowBorderActive: "border-blue-800",
  },
  red: {
    solid: {
      bg: "bg-red-500 border-red-500",
      bgActive: "bg-red-700 border-red-700",
      text: "text-white",
      textActive: "text-red-600",
      shadowBg: "bg-red-700",
      shadowBgActive: "bg-red-800",
    },
    outline: {
      bg: "bg-transparent border-red-500",
      bgActive: "bg-red-500/25 border-red-700",
      text: "text-red-500",
      textActive: "text-red-600",
      shadowBg: "bg-transparent",
      shadowBgActive: "bg-transparent",
    },
    shadowBorder: "border-blue-700",
    shadowBorderActive: "border-blue-900",
  },
};
export function shadowClassName({
  active,
  outline,
  theme,
  shape,
  disabled,
}: Props) {
  // doesn't show a shadow for thin outlines
  if (outline === "thin") {
    return undefined;
  }

  // doesn't show a shadow when transparent
  if (theme === "transparent") {
    return undefined;
  }

  const themeStyle = outline === "thick" ? "outline" : "solid";
  const shared = classNames(
    "absolute inset-0 transition-colors",
    shape === "circle" && "rounded-full",
    theming[theme][themeStyle].shadowBg,
    active && theming[theme][themeStyle].shadowBgActive,
    shadowMakers.shadowTransform({
      active: active === true,
      disabled: !!disabled,
    })
  );

  if (outline === "thick") {
    return classNames(
      shared,
      "border-8",
      active === true
        ? theming[theme].shadowBorderActive
        : theming[theme].shadowBorder
    );
  }

  return classNames(shared);
}

export function faceClassName({
  outline,
  active,
  theme,
  shape,
  disabled,
}: Props) {
  const themeStyle = outline ? "outline" : "solid";
  const activeColors = classNames(
    active === true
      ? theme === "transparent"
        ? ["bg-transparent text-white border-none"]
        : [
            theming[theme][themeStyle].bgActive,
            theming[theme][themeStyle].textActive,
          ]
      : theme === "transparent"
      ? ["bg-transparent text-white border-none"]
      : [theming[theme][themeStyle].bg, theming[theme][themeStyle].text]
  );

  return classNames(
    "absolute inset-0 flex items-center justify-center border-2 p-[15%] transition-all",
    shape === "circle" && "rounded-full",
    outline === "thick" ? "border-8" : "border-2",
    disabled
      ? "bg-gray-500 text-neutral-900 border-gray-500 cursor-not-allowed"
      : activeColors,

    shadowMakers.faceTransform({
      active: active === true,
      disabled: !!disabled,
    })
  );
}
